import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";

const AboutSection = () => {
  const values = [
    {
      icon: "Shield",
      title: "Transparência Total",
      description:
        "Todas as taxas e condições são apresentadas de forma clara, sem letras miúdas ou pegadinhas.",
    },
    {
      icon: "Heart",
      title: "Atendimento Humanizado",
      description:
        "Cada cliente é único. Oferecemos soluções personalizadas com atendimento dedicado e empático.",
    },
    {
      icon: "Award",
      title: "Ética e Responsabilidade",
      description:
        "Priorizamos o bem-estar financeiro dos nossos clientes acima de qualquer interesse comercial.",
    },
    {
      icon: "Users",
      title: "Experiência Comprovada",
      description:
        "Mais de 10 anos no mercado financeiro, com milhares de vidas transformadas positivamente.",
    },
  ];

  const certifications = [
    {
      name: "Banco Central do Brasil",
      description: "Autorização para operações financeiras",
      icon: "Building",
    },
    {
      name: "CNPJ Ativo",
      description: "63.284.659/0001-37",
      icon: "FileText",
    },
    {
      name: "ISO 27001",
      description: "Segurança da informação certificada",
      icon: "Lock",
    },
    {
      name: "LGPD Compliance",
      description: "Proteção de dados pessoais",
      icon: "Shield",
    },
  ];

  return (
    <section id="sobre" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Info" size={16} />
            <span>Sobre a TJEP</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Quem Somos e Por Que Existimos
          </h2>

          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Nascemos com a missão de democratizar o acesso a soluções
            financeiras justas e transparentes para aposentados, pensionistas e
            trabalhadores brasileiros
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-foreground">
                Nossa História e Missão
              </h3>

              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  A TJEP Soluções Financeiras foi fundada em 2014 com um
                  propósito claro: oferecer soluções financeiras éticas e
                  transparentes para brasileiros que mais precisam de apoio
                  financeiro especializado.
                </p>

                <p>
                  Percebemos que aposentados, pensionistas e trabalhadores CLT
                  enfrentavam dificuldades para acessar produtos financeiros
                  adequados às suas necessidades, muitas vezes sendo vítimas de
                  práticas abusivas ou condições desfavoráveis.
                </p>

                <p>
                  Hoje, com mais de 15.000 clientes atendidos em todo o Brasil,
                  continuamos firmes em nossa missão de proporcionar
                  tranquilidade financeira através de soluções personalizadas,
                  atendimento humanizado e total transparência em nossos
                  processos.
                </p>
              </div>
            </div>

            {/* Key Numbers */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-primary/5 rounded-xl p-6 text-center">
                <p className="text-3xl font-bold text-primary mb-2">10+</p>
                <p className="text-sm text-muted-foreground">
                  Anos de Experiência
                </p>
              </div>
              <div className="bg-success/5 rounded-xl p-6 text-center">
                <p className="text-3xl font-bold text-success mb-2">15K+</p>
                <p className="text-sm text-muted-foreground">
                  Clientes Atendidos
                </p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <Image
              src="https://images.unsplash.com/photo-1709978164477-6ba18ff284d7"
              alt="Equipe profissional da TJEP em escritório moderno, demonstrando competência e confiabilidade no atendimento financeiro"
              className="w-full h-auto rounded-2xl shadow-xl"
            />

            {/* Floating Badge */}
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-lg border">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Award" size={24} className="text-primary" />
                </div>
                <div>
                  <p className="font-bold text-foreground">Certificação</p>
                  <p className="text-sm text-muted-foreground">Banco Central</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Nossos Valores e Compromissos
          </h3>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values?.map((value, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto">
                  <Icon
                    name={value?.icon as any}
                    size={32}
                    className="text-primary"
                  />
                </div>
                <h4 className="text-lg font-bold text-foreground">
                  {value?.title}
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {value?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Certifications */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Certificações e Conformidade
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Mantemos as mais altas certificações de segurança e conformidade
              para garantir a proteção e confiança dos nossos clientes
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications?.map((cert, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 text-center shadow-subtle"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon
                    name={cert?.icon as any}
                    size={24}
                    className="text-primary"
                  />
                </div>
                <h4 className="font-bold text-foreground mb-2">{cert?.name}</h4>
                <p className="text-sm text-muted-foreground">
                  {cert?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Team Section */}
        <div className="mt-20 text-center">
          <h3 className="text-2xl font-bold text-foreground mb-8">
            Nossa Equipe Especializada
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white rounded-xl p-6 shadow-subtle">
              <Image
                src="https://images.unsplash.com/photo-1594332326483-553b29961e64"
                alt="Diretor financeiro brasileiro sorrindo, homem de 45 anos usando terno azul marinho em escritório executivo"
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
              />

              <h4 className="font-bold text-foreground">
                Carlos Eduardo Silva
              </h4>
              <p className="text-sm text-primary font-medium">
                Diretor Financeiro
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                15+ anos em consultoria financeira
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-subtle">
              <Image
                src="https://images.unsplash.com/photo-1654874071275-6a313b4ce20c"
                alt="Gerente de relacionamento brasileira sorrindo, mulher de 38 anos usando blazer cinza em ambiente corporativo"
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
              />

              <h4 className="font-bold text-foreground">Ana Paula Rodrigues</h4>
              <p className="text-sm text-primary font-medium">
                Gerente de Relacionamento
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Especialista em atendimento ao cliente
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-subtle">
              <Image
                src="https://images.unsplash.com/photo-1721667351644-d0b215cb61bf"
                alt="Consultor sênior brasileiro sorrindo, homem de 52 anos usando camisa social branca em escritório moderno"
                className="w-20 h-20 rounded-full object-cover mx-auto mb-4"
              />

              <h4 className="font-bold text-foreground">Roberto Santos Lima</h4>
              <p className="text-sm text-primary font-medium">
                Consultor Sênior
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Especialista em benefícios previdenciários
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
